import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { priceListApi, estimateCategoryApi } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import { toast } from 'sonner';
import { Plus, Search, Edit, Trash2, Package, ChevronRight, ChevronDown, Upload, FileSpreadsheet, Check, AlertCircle, Printer, Download, X } from 'lucide-react';
import CategoryNavBar from '../components/shared/CategoryNavBar';
import { CATEGORIES } from '../components/estimates/EstimateCategoryTabs';

const PriceListPage = () => {
  const { isAdmin: _isAdminRaw, isProjectManager } = useAuth();
  // Task B permessi: Project Manager equiparato ad Admin per la gestione del listino prezzi
  const isAdmin = _isAdminRaw || isProjectManager;
  const [priceItems, setPriceItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    description: '',
    unit: '',
    unit_price: '',
    category: '',
    subcategory: '',
    notes: ''
  });

  // Import state
  const fileInputRef = useRef(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importStep, setImportStep] = useState(1); // 1: upload, 2: mapping/sheets, 3: result
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importMapping, setImportMapping] = useState({
    codice: '',
    descrizione: '',
    unita_misura: '',
    prezzo: '',
    categoria: '',
    sottocategoria: '',
    note: ''
  });
  const [importOptions, setImportOptions] = useState({
    categoriaDefault: 'A',
    sottocategoriaDefault: 'Generale',
    sovrascrivi: false
  });
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  
  // Multi-sheet import state
  const [sheetsPreview, setSheetsPreview] = useState(null);
  const [selectedSheets, setSelectedSheets] = useState({}); // {sheetName: {selected: bool, items: [{...item, selected: bool}]}}
  const [expandedSheets, setExpandedSheets] = useState({}); // {sheetName: bool}
  
  // Duplicates state
  const [showDuplicatesModal, setShowDuplicatesModal] = useState(false);
  const [duplicates, setDuplicates] = useState(null);
  const [loadingDuplicates, setLoadingDuplicates] = useState(false);
  const [duplicateSearchType, setDuplicateSearchType] = useState('all');
  const [showDuplicateOptions, setShowDuplicateOptions] = useState(false);

  // Selection & inline edit state
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [editingInlineId, setEditingInlineId] = useState(null);
  const [editingInlineField, setEditingInlineField] = useState(null);
  const [editingInlineValue, setEditingInlineValue] = useState('');
  const [exportingCSV, setExportingCSV] = useState(false);

  // Task B - Reclassify state
  const [usageCount, setUsageCount] = useState(null); // {count, has_legacy_matches}
  const [usageCountLoading, setUsageCountLoading] = useState(false);
  const [reclassifyConfirm, setReclassifyConfirm] = useState(null); // {oldCategory, newCategory, oldSubcategory, newSubcategory, oldCode, pendingData}

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async (preserveFilters = false) => {
    // Salva i filtri correnti prima di ricaricare
    const currentCategory = activeCategory;
    const currentSubcategory = selectedSubcategory;
    
    try {
      setLoading(true);
      const [priceRes, catRes] = await Promise.all([
        priceListApi.getAll(),
        estimateCategoryApi.getAll()
      ]);
      setPriceItems(priceRes.data);
      setCategories(catRes.data);
      
      if (preserveFilters && currentCategory) {
        // Mantieni i filtri esistenti
        setActiveCategory(currentCategory);
        setSelectedSubcategory(currentSubcategory);
      } else {
        // Set first category with items as active (solo al primo caricamento)
        const firstCatWithItems = CATEGORIES.find(cat => 
          priceRes.data.some(item => item.category === cat.code)
        );
        if (firstCatWithItems) {
          setActiveCategory(firstCatWithItems.code);
        } else {
          setActiveCategory(CATEGORIES[0].code);
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Errore nel caricamento del listino');
    } finally {
      setLoading(false);
    }
  };

  // Items count per category
  const categoryCounts = useMemo(() => {
    const counts = {};
    CATEGORIES.forEach(cat => {
      counts[cat.code] = priceItems.filter(item => item.category === cat.code).length;
    });
    return counts;
  }, [priceItems]);

  // Get subcategories for active category
  const activeSubcategories = useMemo(() => {
    const cat = categories.find(c => c.code === activeCategory);
    return cat?.subcategories || [];
  }, [categories, activeCategory]);

  // Mappa codice sottocategoria -> nome (per pill/tooltip tabella)
  const subcategoryNameMap = useMemo(() => {
    const map = {};
    (activeSubcategories || []).forEach((sub) => { map[sub.code] = sub.name; });
    return map;
  }, [activeSubcategories]);

  // Presentation layer: C.01 -> C01 (nessuna modifica ai dati salvati)
  const formatSubcatCode = (code) => (code || '').replace(/\./g, '');

  // Filtered items for active category - SORTED BY SUBCATEGORY AND CODE
  const activeCategoryItems = useMemo(() => {
    if (!activeCategory) return [];
    
    const filtered = priceItems.filter(item => {
      // Must belong to active category
      if (item.category !== activeCategory) return false;
      
      // Filter by search
      const matchesSearch = !searchTerm || 
        item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.code.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Filter by subcategory
      const matchesSubcategory = selectedSubcategory === 'all' || 
        item.subcategory === selectedSubcategory;
      
      return matchesSearch && matchesSubcategory;
    });
    
    // Sort by subcategory code, then by item code
    return filtered.sort((a, b) => {
      // First sort by subcategory
      const subA = a.subcategory || 'ZZZ';
      const subB = b.subcategory || 'ZZZ';
      if (subA !== subB) return subA.localeCompare(subB);
      
      // Then sort by code
      const codeA = a.code || '';
      const codeB = b.code || '';
      return codeA.localeCompare(codeB);
    });
  }, [priceItems, activeCategory, searchTerm, selectedSubcategory]);

  // Items without category (for migration purposes)
  const uncategorizedItems = useMemo(() => {
    return priceItems.filter(item => !item.category);
  }, [priceItems]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Task B: se in edit e la categoria/subcategory sono cambiate,
    // apri il confirm modal per scegliere Mantieni codice / Rigenera codice.
    if (editingItem) {
      const categoryChanged = formData.category !== editingItem.category;
      const subcategoryChanged = formData.subcategory !== (editingItem.subcategory || '');
      if (categoryChanged || subcategoryChanged) {
        setReclassifyConfirm({
          oldCategory: editingItem.category,
          newCategory: formData.category,
          oldSubcategory: editingItem.subcategory || '',
          newSubcategory: formData.subcategory || '',
          oldCode: editingItem.code,
          pendingData: {
            ...formData,
            unit_price: parseFloat(formData.unit_price) || 0,
          },
        });
        return; // il confirm dialog gestira' il salvataggio
      }
    }
    await persistUpdate({ ...formData, unit_price: parseFloat(formData.unit_price) || 0 });
  };

  // Task B: salvataggio effettivo (chiamato da handleSubmit o dal confirm)
  const persistUpdate = async (data) => {
    try {
      if (editingItem) {
        await priceListApi.update(editingItem.id, data);
        toast.success('Voce aggiornata');
      } else {
        await priceListApi.create(data);
        toast.success('Voce creata');
      }
      setShowModal(false);
      setEditingItem(null);
      setReclassifyConfirm(null);
      setUsageCount(null);
      resetForm();
      loadData(true);
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Errore nel salvataggio';
      toast.error(msg);
    }
  };

  // Task B: azioni del confirm modal
  const handleReclassifyKeepCode = async () => {
    if (!reclassifyConfirm) return;
    // Mantieni codice: salva solo con new category/subcategory, code invariato
    await persistUpdate(reclassifyConfirm.pendingData);
  };

  const handleReclassifyRegenerateCode = async () => {
    if (!reclassifyConfirm) return;
    try {
      const { newCategory, newSubcategory } = reclassifyConfirm;
      // Chiama next-code per la nuova categoria/sottocategoria
      const res = await priceListApi.getNextCode(newCategory, newSubcategory || null);
      const nextCode = res?.data?.next_code;
      if (!nextCode) {
        toast.error('Impossibile generare il nuovo codice');
        return;
      }
      await persistUpdate({ ...reclassifyConfirm.pendingData, code: nextCode });
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Errore rigenerando il codice';
      toast.error(msg);
    }
  };

  const handleReclassifyCancel = () => {
    // Chiudi confirm ma resta nel dialog di modifica: l'utente puo' cambiare idea
    setReclassifyConfirm(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa voce?')) return;
    
    try {
      await priceListApi.delete(id);
      toast.success('Voce eliminata');
      loadData(true); // Preserva i filtri correnti
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  // === DUPLICATES FUNCTIONS ===
  const handleFindDuplicates = async () => {
    setLoadingDuplicates(true);
    try {
      const res = await priceListApi.findDuplicates(duplicateSearchType);
      setDuplicates(res.data);
      setShowDuplicatesModal(true);
      setShowDuplicateOptions(false);
    } catch (error) {
      toast.error('Errore nella ricerca duplicati');
    } finally {
      setLoadingDuplicates(false);
    }
  };

  const handleDeleteDuplicate = async (id) => {
    try {
      await priceListApi.delete(id);
      toast.success('Voce eliminata');
      // Ricarica duplicati con lo stesso tipo di ricerca
      const res = await priceListApi.findDuplicates(duplicateSearchType);
      setDuplicates(res.data);
      loadData(true);
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  // === EXPORT CSV ===
  const handleExportCSV = async (categoryFilter = null) => {
    setExportingCSV(true);
    try {
      await priceListApi.exportCSV(categoryFilter);
      toast.success('Export CSV completato');
    } catch (error) {
      toast.error('Errore nell\'export');
    } finally {
      setExportingCSV(false);
    }
  };

  // === INLINE EDIT ===
  const handleStartInlineEdit = (itemId, field, currentValue) => {
    setEditingInlineId(itemId);
    setEditingInlineField(field);
    setEditingInlineValue(currentValue?.toString() || '');
  };

  const handleCancelInlineEdit = () => {
    setEditingInlineId(null);
    setEditingInlineField(null);
    setEditingInlineValue('');
  };

  const handleSaveInlineEdit = async () => {
    if (!editingInlineId || !editingInlineField) return;
    
    try {
      await priceListApi.updateInline(editingInlineId, editingInlineField, editingInlineValue);
      toast.success('Aggiornato');
      handleCancelInlineEdit();
      loadData(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'aggiornamento');
    }
  };

  // === SELECTION & BULK DELETE ===
  const handleToggleSelection = (itemId) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedItems.size === activeCategoryItems.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(activeCategoryItems.map(i => i.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedItems.size === 0) return;
    
    if (!window.confirm(`Sei sicuro di voler eliminare ${selectedItems.size} voci?`)) return;
    
    try {
      await priceListApi.bulkDelete(Array.from(selectedItems));
      toast.success(`Eliminate ${selectedItems.size} voci`);
      setSelectedItems(new Set());
      loadData(true);
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  // === PRINT FUNCTIONS ===
  const handlePrintCategory = () => {
    if (!activeCategory || activeCategoryItems.length === 0) {
      toast.error('Seleziona una categoria con voci da stampare');
      return;
    }

    const categoryInfo = CATEGORIES.find(c => c.code === activeCategory);
    const categoryName = categoryInfo ? `${activeCategory} - ${categoryInfo.label}` : activeCategory;
    
    // Raggruppa per sottocategoria
    const itemsBySubcategory = {};
    activeCategoryItems.forEach(item => {
      const subcat = item.subcategory || 'Altro';
      if (!itemsBySubcategory[subcat]) {
        itemsBySubcategory[subcat] = [];
      }
      itemsBySubcategory[subcat].push(item);
    });

    // Trova i nomi delle sottocategorie
    const activeSubcats = categories.find(c => c.code === activeCategory)?.subcategories || [];
    const getSubcategoryName = (code) => {
      const sub = activeSubcats.find(s => s.code === code);
      return sub ? `${sub.code} - ${sub.name}` : code;
    };

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Listino Prezzi - ${categoryName}</title>
        <style>
          @page { 
            size: A4; 
            margin: 15mm; 
          }
          * { 
            box-sizing: border-box; 
            margin: 0; 
            padding: 0; 
          }
          body { 
            font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif; 
            font-size: 9pt; 
            line-height: 1.4;
            color: #333;
          }
          .header { 
            display: flex;
            justify-content: space-between;
            align-items: center;
            border-bottom: 3px solid #FF7819; 
            padding-bottom: 10px; 
            margin-bottom: 15px; 
          }
          .header h1 { 
            font-size: 16pt; 
            color: #FF7819; 
            font-weight: 700;
          }
          .header .date {
            font-size: 9pt;
            color: #666;
          }
          .category-badge {
            background: #FF7819;
            color: white;
            padding: 4px 12px;
            border-radius: 4px;
            font-weight: 600;
            font-size: 11pt;
          }
          .subcategory-header {
            background: #f5f5f5;
            padding: 6px 10px;
            margin: 12px 0 6px 0;
            font-weight: 600;
            font-size: 10pt;
            border-left: 3px solid #FF7819;
          }
          table { 
            width: 100%; 
            border-collapse: collapse; 
            margin-bottom: 10px;
          }
          th { 
            background: #FF7819; 
            color: white; 
            padding: 6px 8px; 
            text-align: left; 
            font-weight: 600;
            font-size: 8pt;
            text-transform: uppercase;
          }
          th.price { text-align: right; }
          th.unit { text-align: center; width: 60px; }
          th.code { width: 80px; }
          td { 
            padding: 5px 8px; 
            border-bottom: 1px solid #e0e0e0; 
            vertical-align: top;
          }
          td.code { 
            font-family: monospace; 
            font-size: 8pt;
            color: #666;
          }
          td.unit { text-align: center; }
          td.price { 
            text-align: right; 
            font-weight: 500;
            white-space: nowrap;
          }
          tr:nth-child(even) { background: #fafafa; }
          .notes {
            font-size: 8pt;
            color: #FF7819;
            font-style: italic;
            margin-top: 2px;
          }
          .footer { 
            margin-top: 20px; 
            padding-top: 10px; 
            border-top: 1px solid #ddd; 
            font-size: 8pt; 
            color: #666;
            display: flex;
            justify-content: space-between;
          }
          .total-items {
            font-weight: 600;
          }
          @media print {
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div>
            <h1>ABITALO - Listino Prezzi</h1>
            <span class="category-badge">${categoryName}</span>
          </div>
          <div class="date">
            Stampato il ${new Date().toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' })}
          </div>
        </div>
        
        ${Object.entries(itemsBySubcategory).sort(([a], [b]) => a.localeCompare(b)).map(([subcat, items]) => `
          <div class="subcategory-header">${getSubcategoryName(subcat)}</div>
          <table>
            <thead>
              <tr>
                <th class="code">Codice</th>
                <th>Descrizione</th>
                <th class="unit">U.M.</th>
                <th class="price">Prezzo</th>
              </tr>
            </thead>
            <tbody>
              ${items.map(item => `
                <tr>
                  <td class="code">${item.code || '-'}</td>
                  <td>
                    ${item.description}
                    ${item.notes ? `<div class="notes">Nota: ${item.notes}</div>` : ''}
                  </td>
                  <td class="unit">${item.unit || '-'}</td>
                  <td class="price">€ ${item.unit_price?.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0,00'}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        `).join('')}
        
        <div class="footer">
          <span class="total-items">Totale voci: ${activeCategoryItems.length}</span>
          <span>ABITALO - L&F Costruzioni S.r.l.</span>
        </div>
      </body>
      </html>
    `;

    // Usa iframe nascosto per stampa sicura (evita document.write XSS)
    const printFrame = document.createElement('iframe');
    printFrame.style.position = 'absolute';
    printFrame.style.width = '0';
    printFrame.style.height = '0';
    printFrame.style.border = 'none';
    document.body.appendChild(printFrame);
    
    const frameDoc = printFrame.contentDocument || printFrame.contentWindow.document;
    frameDoc.open();
    frameDoc.write(printContent);
    frameDoc.close();
    
    // Aspetta che il contenuto sia caricato prima di stampare
    printFrame.onload = () => {
      setTimeout(() => {
        printFrame.contentWindow.focus();
        printFrame.contentWindow.print();
        // Rimuovi iframe dopo la stampa
        setTimeout(() => document.body.removeChild(printFrame), 1000);
      }, 250);
    };
  };

  // === IMPORT FUNCTIONS ===
  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const validTypes = ['.csv', '.xlsx', '.xls'];
    const ext = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
    if (!validTypes.includes(ext)) {
      toast.error('Formato non supportato. Usa CSV o Excel (.xlsx)');
      return;
    }
    
    setImportFile(file);
    setImporting(true);
    
    try {
      // Per file Excel, usa la preview multi-foglio
      if (ext === '.xlsx' || ext === '.xls') {
        const res = await priceListApi.previewSheets(file);
        setSheetsPreview(res.data);
        
        // Inizializza selezione: tutti i fogli selezionati con tutte le voci
        const initialSelection = {};
        const initialExpanded = {};
        res.data.sheets.forEach(sheet => {
          initialSelection[sheet.sheet_name] = {
            selected: true,
            items: sheet.items.map(item => ({ ...item, selected: true }))
          };
          initialExpanded[sheet.sheet_name] = false;
        });
        setSelectedSheets(initialSelection);
        setExpandedSheets(initialExpanded);
        
        // Auto-mappa colonne
        const cols = res.data.columns || [];
        const autoMap = { ...importMapping };
        cols.forEach(col => {
          const colLower = (col || '').toLowerCase();
          if (colLower.includes('codice') || colLower.includes('cod') || colLower === 'code') {
            autoMap.codice = col;
          } else if (colLower.includes('descrizione') || colLower.includes('desc') || colLower === 'description') {
            autoMap.descrizione = col;
          } else if (colLower.includes('unità') || colLower.includes('unita') || colLower.includes('u.m.') || colLower === 'um' || colLower === 'unit') {
            autoMap.unita_misura = col;
          } else if (colLower.includes('prezzo') || colLower.includes('importo') || colLower.includes('costo') || colLower === 'price') {
            autoMap.prezzo = col;
          } else if (colLower.includes('sottocategoria') || colLower.includes('sub')) {
            autoMap.sottocategoria = col;
          }
        });
        setImportMapping(autoMap);
        setImportStep(2);
      } else {
        // Per CSV, usa la preview standard
        const res = await priceListApi.previewImport(file);
        setImportPreview(res.data);
        setSheetsPreview(null);
        
        const cols = res.data.columns || [];
        const autoMap = { ...importMapping };
        cols.forEach(col => {
          const colLower = col.toLowerCase();
          if (colLower.includes('codice') || colLower.includes('cod') || colLower === 'code') {
            autoMap.codice = col;
          } else if (colLower.includes('descrizione') || colLower.includes('desc') || colLower === 'description') {
            autoMap.descrizione = col;
          } else if (colLower.includes('unità') || colLower.includes('unita') || colLower.includes('u.m.') || colLower === 'um' || colLower === 'unit') {
            autoMap.unita_misura = col;
          } else if (colLower.includes('prezzo') || colLower.includes('importo') || colLower.includes('costo') || colLower === 'price') {
            autoMap.prezzo = col;
          } else if (colLower.includes('categoria') || colLower.includes('capitolo') || colLower === 'category') {
            autoMap.categoria = col;
          } else if (colLower.includes('sottocategoria') || colLower.includes('sub')) {
            autoMap.sottocategoria = col;
          } else if (colLower.includes('note') || colLower.includes('nota')) {
            autoMap.note = col;
          }
        });
        setImportMapping(autoMap);
        setImportStep(2);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella lettura del file');
    } finally {
      setImporting(false);
    }
  };

  // Gestione selezione fogli
  const toggleSheetSelection = (sheetName) => {
    setSelectedSheets(prev => {
      const sheet = prev[sheetName];
      const newSelected = !sheet.selected;
      return {
        ...prev,
        [sheetName]: {
          ...sheet,
          selected: newSelected,
          items: sheet.items.map(item => ({ ...item, selected: newSelected }))
        }
      };
    });
  };

  const toggleItemSelection = (sheetName, itemIndex) => {
    setSelectedSheets(prev => {
      const sheet = prev[sheetName];
      const newItems = [...sheet.items];
      newItems[itemIndex] = { ...newItems[itemIndex], selected: !newItems[itemIndex].selected };
      const allSelected = newItems.every(item => item.selected);
      const someSelected = newItems.some(item => item.selected);
      return {
        ...prev,
        [sheetName]: {
          ...sheet,
          selected: allSelected,
          items: newItems
        }
      };
    });
  };

  const toggleExpandSheet = (sheetName) => {
    setExpandedSheets(prev => ({
      ...prev,
      [sheetName]: !prev[sheetName]
    }));
  };

  const selectAllSheets = (select) => {
    setSelectedSheets(prev => {
      const newState = {};
      Object.keys(prev).forEach(sheetName => {
        newState[sheetName] = {
          ...prev[sheetName],
          selected: select,
          items: prev[sheetName].items.map(item => ({ ...item, selected: select }))
        };
      });
      return newState;
    });
  };

  // Conta voci selezionate
  const getSelectedCount = () => {
    let total = 0;
    let selected = 0;
    Object.values(selectedSheets).forEach(sheet => {
      sheet.items.forEach(item => {
        total++;
        if (item.selected) selected++;
      });
    });
    return { total, selected };
  };

  // Import multi-foglio
  const handleImportSheets = async () => {
    setImporting(true);
    try {
      // Prepara dati per l'import
      const sheetsToImport = [];
      Object.entries(selectedSheets).forEach(([sheetName, sheet]) => {
        const selectedItems = sheet.items.filter(item => item.selected);
        if (selectedItems.length > 0) {
          const sheetInfo = sheetsPreview.sheets.find(s => s.sheet_name === sheetName);
          sheetsToImport.push({
            sheet_name: sheetName,
            category_code: sheetInfo?.category_code || 'A',
            selected_items: selectedItems
          });
        }
      });

      if (sheetsToImport.length === 0) {
        toast.error('Seleziona almeno una voce da importare');
        setImporting(false);
        return;
      }

      const res = await priceListApi.importSheets({
        sheets: sheetsToImport,
        mapping: importMapping,
        sovrascrivi_esistenti: importOptions.sovrascrivi
      });
      
      setImportResult(res.data);
      setImportStep(3);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante l\'import');
    } finally {
      setImporting(false);
    }
  };

  const handleImport = async () => {
    if (!importMapping.codice || !importMapping.descrizione || !importMapping.prezzo) {
      toast.error('Mappa almeno: Codice, Descrizione e Prezzo');
      return;
    }
    
    setImporting(true);
    try {
      const res = await priceListApi.importFile(
        importFile,
        importMapping,
        importOptions.categoriaDefault,
        importOptions.sottocategoriaDefault,
        importOptions.sovrascrivi
      );
      setImportResult(res.data);
      setImportStep(3);
      loadData(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante l\'import');
    } finally {
      setImporting(false);
    }
  };

  const resetImport = () => {
    setShowImportModal(false);
    setImportStep(1);
    setImportFile(null);
    setImportPreview(null);
    setSheetsPreview(null);
    setSelectedSheets({});
    setExpandedSheets({});
    setImportMapping({
      codice: '',
      descrizione: '',
      unita_misura: '',
      prezzo: '',
      categoria: '',
      sottocategoria: '',
      note: ''
    });
    setImportResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Genera codice automatico quando cambia categoria o sottocategoria
  const generateNextCode = async (category, subcategory) => {
    if (!category) return;
    try {
      const response = await priceListApi.getNextCode(category, subcategory || null);
      setFormData(prev => ({
        ...prev,
        code: response.data.next_code
      }));
    } catch (error) {
      console.error('Error generating next code:', error);
    }
  };

  const resetForm = async () => {
    const initialCategory = activeCategory || '';
    setFormData({
      code: '',
      description: '',
      unit: '',
      unit_price: '',
      category: initialCategory,
      subcategory: '',
      notes: ''
    });
    
    // Genera codice automatico se c'è una categoria
    if (initialCategory) {
      setTimeout(() => generateNextCode(initialCategory, null), 100);
    }
  };

  // Handler per cambio categoria nel form
  const handleCategoryChange = (value) => {
    setFormData(prev => ({ 
      ...prev, 
      category: value,
      subcategory: '' // Reset subcategory when category changes
    }));
    // Genera nuovo codice per la categoria
    if (!editingItem) {
      generateNextCode(value, null);
    }
  };

  // Handler per cambio sottocategoria nel form
  const handleSubcategoryChange = (value) => {
    setFormData(prev => ({ 
      ...prev, 
      subcategory: value
    }));
    // Genera nuovo codice per la sottocategoria
    if (!editingItem) {
      generateNextCode(formData.category, value);
    }
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setFormData({
      code: item.code,
      description: item.description,
      unit: item.unit,
      unit_price: item.unit_price ? item.unit_price.toString() : '',
      category: item.category || '',
      subcategory: item.subcategory || '',
      notes: item.notes || ''
    });
    setShowModal(true);
    // Task B: fetch conteggio utilizzi per informare l'utente
    setUsageCount(null);
    setUsageCountLoading(true);
    priceListApi.getUsageCount(item.id)
      .then((res) => setUsageCount(res.data))
      .catch(() => setUsageCount(null))
      .finally(() => setUsageCountLoading(false));
  };

  const openNewModal = async () => {
    setEditingItem(null);
    const initialCategory = activeCategory || '';
    setFormData({
      code: '',
      description: '',
      unit: '',
      unit_price: '',
      category: initialCategory,
      subcategory: '',
      notes: ''
    });
    setShowModal(true);
    
    // Genera codice automatico se c'è una categoria
    if (initialCategory) {
      setTimeout(() => generateNextCode(initialCategory, null), 100);
    }
  };

  // Get subcategories for form based on selected category
  const formSubcategories = useMemo(() => {
    const cat = categories.find(c => c.code === formData.category);
    return cat?.subcategories || [];
  }, [categories, formData.category]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-[#FF7819] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="price-list-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-h2">Listino Prezzi</h1>
          <p className="text-body-small mt-1">
            Gestisci le voci del listino prezzi ({priceItems.length} voci totali)
          </p>
        </div>
        {isAdmin && (
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowImportModal(true)}
              data-testid="import-price-list-btn"
              className="rounded-lg"
            >
              <Upload className="w-4 h-4 mr-2" />
              Importa Listino
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowDuplicateOptions(true)}
              disabled={loadingDuplicates}
              data-testid="find-duplicates-btn"
              className="rounded-lg"
            >
              <Search className="w-4 h-4 mr-2" />
              {loadingDuplicates ? 'Cerco...' : 'Trova Duplicati'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleExportCSV(activeCategory)}
              disabled={exportingCSV}
              data-testid="export-csv-btn"
              className="rounded-lg"
            >
              <Download className="w-4 h-4 mr-2" />
              {exportingCSV ? 'Esporto...' : 'Export CSV'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrintCategory}
              disabled={!activeCategory || activeCategoryItems.length === 0}
              data-testid="print-category-btn"
              className="rounded-lg"
            >
              <Printer className="w-4 h-4 mr-2" />
              Stampa
            </Button>
            <Button
              size="sm"
              className="btn-primary rounded-lg"
              onClick={openNewModal}
              data-testid="new-price-item-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nuova Voce
            </Button>
          </div>
        )}
      </div>

      {/* Duplicate Options Modal */}
      <Dialog open={showDuplicateOptions} onOpenChange={setShowDuplicateOptions}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Search className="w-5 h-5 text-amber-500" />
              Cerca Duplicati
            </DialogTitle>
            <DialogDescription>
              Scegli il tipo di ricerca per trovare voci duplicate
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 py-4">
            <label 
              className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                duplicateSearchType === 'same_category' ? 'border-amber-500 bg-amber-50' : 'border-gray-200 hover:border-amber-300'
              }`}
              onClick={() => setDuplicateSearchType('same_category')}
            >
              <input 
                type="radio" 
                name="duplicateType" 
                checked={duplicateSearchType === 'same_category'}
                onChange={() => setDuplicateSearchType('same_category')}
                className="mt-1 accent-amber-500"
              />
              <div>
                <div className="font-medium">Stessa categoria</div>
                <div className="text-sm text-[#737373]">
                  Trova voci con la stessa descrizione <strong>nella stessa categoria</strong>
                  <br />(es. due "Demolizione muratura" entrambe in C)
                </div>
              </div>
            </label>
            
            <label 
              className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                duplicateSearchType === 'different_category' ? 'border-amber-500 bg-amber-50' : 'border-gray-200 hover:border-amber-300'
              }`}
              onClick={() => setDuplicateSearchType('different_category')}
            >
              <input 
                type="radio" 
                name="duplicateType" 
                checked={duplicateSearchType === 'different_category'}
                onChange={() => setDuplicateSearchType('different_category')}
                className="mt-1 accent-amber-500"
              />
              <div>
                <div className="font-medium">Categorie diverse</div>
                <div className="text-sm text-[#737373]">
                  Trova voci con la stessa descrizione <strong>in categorie diverse</strong>
                  <br />(es. "Piastrelle" presente sia in C che in F)
                </div>
              </div>
            </label>
            
            <label 
              className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                duplicateSearchType === 'all' ? 'border-amber-500 bg-amber-50' : 'border-gray-200 hover:border-amber-300'
              }`}
              onClick={() => setDuplicateSearchType('all')}
            >
              <input 
                type="radio" 
                name="duplicateType" 
                checked={duplicateSearchType === 'all'}
                onChange={() => setDuplicateSearchType('all')}
                className="mt-1 accent-amber-500"
              />
              <div>
                <div className="font-medium">Tutti i duplicati</div>
                <div className="text-sm text-[#737373]">
                  Trova <strong>tutte le voci duplicate</strong> indipendentemente dalla categoria
                </div>
              </div>
            </label>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDuplicateOptions(false)}>
              Annulla
            </Button>
            <Button 
              className="bg-amber-500 hover:bg-amber-600 text-white"
              onClick={handleFindDuplicates}
              disabled={loadingDuplicates}
            >
              {loadingDuplicates ? 'Cerco...' : 'Cerca Duplicati'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Search & Subcategory Filter — sopra la barra categorie */}
      <Card className="card-abitalo">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#737373]" />
              <Input
                placeholder="Cerca per codice o descrizione..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-abitalo pl-10"
                data-testid="price-list-search"
              />
            </div>
            {activeSubcategories.length > 0 && (
              <Select value={selectedSubcategory} onValueChange={setSelectedSubcategory}>
                <SelectTrigger className="w-full sm:w-64" data-testid="price-list-subcategory-filter">
                  <SelectValue placeholder="Sottocategoria" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte le sottocategorie</SelectItem>
                  {activeSubcategories.map((sub) => (
                    <SelectItem key={sub.code} value={sub.code}>
                      {formatSubcatCode(sub.code)} · {sub.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Category Navigation — stesso linguaggio visivo del Preventivatore */}
      <CategoryNavBar
        categories={CATEGORIES}
        activeCategory={activeCategory}
        counts={categoryCounts}
        onSelect={(code) => {
          setActiveCategory(code);
          setSelectedSubcategory('all');
          setSearchTerm('');
        }}
      />

      {/* Active Category Content */}
      {activeCategory && (
        <Card className="card-abitalo overflow-hidden border-l-4 border-l-[#FF7819]">
          {/* Category Header — stile Preventivatore */}
          <div className="px-5 py-3 flex flex-wrap items-center justify-between gap-2 bg-[#FF7819]">
            <span className="flex items-baseline gap-2 text-white min-w-0">
              <span className="text-[15px] font-semibold text-white/70">{activeCategory}</span>
              <span className="text-white/50">·</span>
              <span className="text-[17px] sm:text-[19px] font-semibold tracking-tight truncate">
                {CATEGORIES.find(c => c.code === activeCategory)?.label}
              </span>
              <span className="ml-2 px-2 py-0.5 bg-white/20 rounded-full text-[12px] sm:text-[13px] font-medium self-center whitespace-nowrap">
                {activeCategoryItems.length} voci
              </span>
            </span>
            {isAdmin && (
              <Button
                size="sm"
                variant="secondary"
                onClick={openNewModal}
                className="bg-white text-[#FF7819] hover:bg-white/90"
                data-testid="category-add-item-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                Aggiungi voce
              </Button>
            )}
          </div>

          {/* Bulk Actions Bar */}
          {isAdmin && selectedItems.size > 0 && (
            <div className="px-4 py-2 bg-red-50 border-b border-red-200 flex items-center justify-between">
              <span className="text-sm text-red-700">
                <strong>{selectedItems.size}</strong> {selectedItems.size === 1 ? 'voce selezionata' : 'voci selezionate'}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedItems(new Set())}
                  className="text-gray-600"
                >
                  <X className="w-4 h-4 mr-1" />
                  Annulla
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleBulkDelete}
                  className="bg-red-600 hover:bg-red-700"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Elimina Selezionate
                </Button>
              </div>
            </div>
          )}

          {/* Items Table */}
          <CardContent className="p-0">
            <div className="table-responsive">
              <Table>
                <TableHeader>
                  <TableRow className="table-header bg-gray-50">
                    {isAdmin && (
                      <TableHead className="p-4 w-10">
                        <Checkbox
                          checked={selectedItems.size === activeCategoryItems.length && activeCategoryItems.length > 0}
                          onCheckedChange={handleSelectAll}
                          aria-label="Seleziona tutti"
                        />
                      </TableHead>
                    )}
                    <TableHead className="p-4 w-24">Codice</TableHead>
                    <TableHead className="p-4">Descrizione</TableHead>
                    <TableHead className="p-4 w-32">Sottocategoria</TableHead>
                    <TableHead className="p-4 w-20">U.M.</TableHead>
                    <TableHead className="p-4 w-28 text-right">Prezzo</TableHead>
                    {isAdmin && <TableHead className="p-4 w-24 text-right">Azioni</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {activeCategoryItems.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={isAdmin ? 7 : 5} className="text-center py-12">
                        <div className="flex flex-col items-center gap-3 text-gray-400">
                          <Package className="w-12 h-12" />
                          <p>Nessuna voce in questa categoria</p>
                          {isAdmin && (
                            <Button 
                              variant="outline" 
                              onClick={openNewModal}
                              className="mt-2"
                            >
                              <Plus className="w-4 h-4 mr-2" />
                              Aggiungi la prima voce
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    activeCategoryItems.map((item, index) => (
                      <TableRow 
                        key={item.id} 
                        className={`table-row hover:bg-orange-50/30 transition-colors ${selectedItems.has(item.id) ? 'bg-orange-50' : ''}`}
                      >
                        {isAdmin && (
                          <TableCell className="table-cell">
                            <Checkbox
                              checked={selectedItems.has(item.id)}
                              onCheckedChange={() => handleToggleSelection(item.id)}
                              aria-label={`Seleziona ${item.code}`}
                            />
                          </TableCell>
                        )}
                        <TableCell className="table-cell font-mono text-[12px] font-semibold text-[#8A8A8A] tracking-tight align-top">
                          {item.code}
                        </TableCell>
                        <TableCell className="table-cell">
                          <div>
                            <p className="text-[14px] font-medium text-[#1F1F1F] leading-snug">{item.description}</p>
                            {item.notes && (
                              <p className="text-xs text-[#FF7819] mt-1 italic bg-[#FFF0E5] px-2 py-1 rounded inline-block">
                                <strong>Nota:</strong> {item.notes}
                              </p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="table-cell">
                          {item.subcategory ? (
                            <span
                              className="inline-flex items-center text-[11px] font-mono font-medium text-[#6B6B6B] bg-[#F3F3F3] border border-[#E5E5E5] px-2 py-0.5 rounded-full whitespace-nowrap"
                              title={subcategoryNameMap[item.subcategory] || item.subcategory}
                            >
                              {formatSubcatCode(item.subcategory)}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">-</span>
                          )}
                        </TableCell>
                        <TableCell className="table-cell text-[#737373] text-center text-sm">
                          {item.unit}
                        </TableCell>
                        <TableCell className="table-cell text-right font-semibold">
                          {editingInlineId === item.id && editingInlineField === 'unit_price' ? (
                            <div className="flex items-center gap-1 justify-end">
                              <Input
                                type="text"
                                value={editingInlineValue}
                                onChange={(e) => setEditingInlineValue(e.target.value)}
                                className="w-24 h-7 text-right text-sm"
                                autoFocus
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleSaveInlineEdit();
                                  if (e.key === 'Escape') handleCancelInlineEdit();
                                }}
                              />
                              <Button size="sm" variant="ghost" onClick={handleSaveInlineEdit} className="h-7 w-7 p-0">
                                <Check className="w-3 h-3 text-green-600" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={handleCancelInlineEdit} className="h-7 w-7 p-0">
                                <X className="w-3 h-3 text-red-600" />
                              </Button>
                            </div>
                          ) : item.category === 'B' && (!item.unit_price || item.unit_price === 0) ? (
                            <span className="text-amber-600 text-xs font-normal">A cura del tecnico</span>
                          ) : (
                            <span 
                              className={`text-[#FF7819] ${isAdmin ? 'cursor-pointer hover:underline' : ''}`}
                              onClick={() => isAdmin && handleStartInlineEdit(item.id, 'unit_price', item.unit_price)}
                              title={isAdmin ? 'Clicca per modificare' : ''}
                            >
                              {formatCurrency(item.unit_price)}
                            </span>
                          )}
                        </TableCell>
                        {isAdmin && (
                          <TableCell className="table-cell text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEditModal(item)}
                                className="h-8 w-8 p-0 hover:bg-blue-50 hover:text-blue-600"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(item.id)}
                                className="h-8 w-8 p-0 hover:bg-red-50 hover:text-red-600"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Uncategorized Items Warning */}
      {uncategorizedItems.length > 0 && isAdmin && (
        <Card className="card-abitalo border-yellow-300 bg-yellow-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center flex-shrink-0">
                <ChevronRight className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <h3 className="font-semibold text-yellow-800">
                  {uncategorizedItems.length} voci senza categoria
                </h3>
                <p className="text-sm text-yellow-700 mt-1">
                  Alcune voci del listino non hanno ancora una categoria assegnata. 
                  Clicca su "Modifica" per assegnare la categoria corretta.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {uncategorizedItems.slice(0, 5).map(item => (
                    <button
                      key={item.id}
                      onClick={() => openEditModal(item)}
                      className="text-xs bg-white px-2 py-1 rounded border border-yellow-300 hover:bg-yellow-100 transition-colors"
                    >
                      {item.code}: {item.description.substring(0, 30)}...
                    </button>
                  ))}
                  {uncategorizedItems.length > 5 && (
                    <span className="text-xs text-yellow-600">
                      +{uncategorizedItems.length - 5} altre
                    </span>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-h3">
              {editingItem ? 'Modifica Voce' : 'Nuova Voce'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Task B: warning + usage counter (solo in modalita' modifica) */}
            {editingItem && (
              <div
                className="bg-[#fafafa] border border-[#e5e7eb] border-l-2 border-l-[#FF7819] rounded-md p-3 text-xs text-[#3C3C3C] space-y-1"
                data-testid="reclassify-warning-box"
              >
                <div className="font-medium text-[#3C3C3C]">
                  La modifica sara&apos; applicata solo ai futuri inserimenti in preventivo. I preventivi, contratti, progetti e SAL gia&apos; esistenti non verranno modificati.
                </div>
                <div className="text-[#737373]" data-testid="reclassify-usage-count">
                  {usageCountLoading && 'Verifica utilizzi in corso…'}
                  {!usageCountLoading && usageCount && usageCount.count > 0 && (
                    <>
                      Voce gia&apos; utilizzata in <span className="font-semibold text-[#FF7819]">{usageCount.count}</span> {usageCount.count === 1 ? 'preventivo' : 'preventivi'}
                      {usageCount.has_legacy_matches && (
                        <span className="text-[10px] ml-1">(inclusi match legacy per codice)</span>
                      )}
                      .
                    </>
                  )}
                  {!usageCountLoading && usageCount && usageCount.count === 0 && (
                    <>Voce non ancora utilizzata in preventivi.</>
                  )}
                </div>
              </div>
            )}
            {/* Category and Subcategory */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Categoria *</Label>
                <Select
                  value={formData.category}
                  onValueChange={handleCategoryChange}
                >
                  <SelectTrigger data-testid="price-item-category">
                    <SelectValue placeholder="Seleziona categoria" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((cat) => (
                      <SelectItem key={cat.code} value={cat.code}>
                        {cat.code} - {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Sottocategoria</Label>
                <Select
                  value={formData.subcategory}
                  onValueChange={handleSubcategoryChange}
                  disabled={!formData.category}
                >
                  <SelectTrigger data-testid="price-item-subcategory">
                    <SelectValue placeholder={formData.category ? "Seleziona" : "Prima seleziona categoria"} />
                  </SelectTrigger>
                  <SelectContent>
                    {formSubcategories.map((sub) => (
                      <SelectItem key={sub.code} value={sub.code}>
                        {sub.code} - {sub.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Code and Description */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Codice * <span className="text-xs text-gray-400">(auto)</span></Label>
                <Input
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  placeholder="Generato automaticamente"
                  className="input-abitalo font-mono bg-gray-50"
                  required
                  data-testid="price-item-code"
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label>Descrizione *</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="input-abitalo min-h-[180px] resize-y text-sm"
                  required
                  placeholder="Inserisci la descrizione completa della voce..."
                  data-testid="price-item-description"
                />
              </div>
            </div>

            {/* Unit, Price, VAT */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Unità di misura *</Label>
                <Select
                  value={formData.unit}
                  onValueChange={(value) => setFormData({ ...formData, unit: value })}
                >
                  <SelectTrigger data-testid="price-item-unit">
                    <SelectValue placeholder="Seleziona" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mq">mq (metri quadri)</SelectItem>
                    <SelectItem value="ml">ml (metri lineari)</SelectItem>
                    <SelectItem value="mc">mc (metri cubi)</SelectItem>
                    <SelectItem value="nr">nr (numero)</SelectItem>
                    <SelectItem value="cad">cad (cadauno)</SelectItem>
                    <SelectItem value="corpo">corpo</SelectItem>
                    <SelectItem value="kg">kg</SelectItem>
                    <SelectItem value="h">h (ore)</SelectItem>
                    <SelectItem value="giorno">giorno</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2 space-y-2">
                <Label>
                  Prezzo unitario (€) {formData.category !== 'B' && '*'}
                  {formData.category === 'B' && (
                    <span className="text-xs text-amber-600 ml-1">(a cura del tecnico)</span>
                  )}
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.unit_price}
                  onChange={(e) => setFormData({ ...formData, unit_price: e.target.value })}
                  className={`input-abitalo ${formData.category === 'B' ? 'bg-amber-50 border-amber-200' : ''}`}
                  placeholder={formData.category === 'B' ? 'Da definire in preventivo' : '0.00'}
                  required={formData.category !== 'B'}
                  data-testid="price-item-price"
                />
              </div>
            </div>

            {/* Info box per categoria B */}
            {formData.category === 'B' && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                <strong>Nota:</strong> Per le pratiche, il prezzo viene definito dal tecnico direttamente nel preventivo in base alla complessità dell'intervento.
              </div>
            )}

            {/* Notes */}
            <div className="space-y-2">
              <Label>Note per uso interno</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="input-abitalo min-h-[80px]"
                placeholder="Es: Prezzo variabile in base alla metratura, richiedere preventivo specifico..."
              />
              <p className="text-xs text-[#737373]">
                Queste note saranno visibili solo internamente quando si aggiunge la voce a un preventivo.
              </p>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Annulla
              </Button>
              <Button 
                type="submit" 
                className="btn-primary" 
                data-testid="submit-price-item"
                disabled={
                  !formData.category || 
                  !formData.code || 
                  !formData.description || 
                  !formData.unit || 
                  (formData.category !== 'B' && !formData.unit_price)
                }
              >
                {editingItem ? 'Aggiorna' : 'Crea'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Task B: Reclassify Confirm Dialog */}
      <Dialog open={!!reclassifyConfirm} onOpenChange={(open) => !open && handleReclassifyCancel()}>
        <DialogContent className="sm:max-w-md" data-testid="reclassify-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-h3">Conferma riclassifica voce</DialogTitle>
            <DialogDescription className="pt-2 text-sm text-[#3C3C3C] space-y-2">
              {reclassifyConfirm && (
                <>
                  <div>
                    Stai spostando la voce <span className="font-mono font-semibold">{reclassifyConfirm.oldCode}</span> da{' '}
                    <span className="font-semibold">
                      {reclassifyConfirm.oldCategory}
                      {reclassifyConfirm.oldSubcategory ? ` / ${reclassifyConfirm.oldSubcategory}` : ''}
                    </span>{' '}
                    a{' '}
                    <span className="font-semibold text-[#FF7819]">
                      {reclassifyConfirm.newCategory}
                      {reclassifyConfirm.newSubcategory ? ` / ${reclassifyConfirm.newSubcategory}` : ''}
                    </span>.
                  </div>
                  <div className="text-[#737373]">
                    Il codice attuale <span className="font-mono">{reclassifyConfirm.oldCode}</span> non verra&apos; rigenerato automaticamente. Vuoi mantenerlo o rigenerare un nuovo codice coerente con la nuova categoria?
                  </div>
                  <div className="text-[10px] text-[#737373]">
                    La modifica non altera preventivi, contratti, progetti o SAL gia&apos; esistenti.
                  </div>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex flex-col sm:flex-row gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleReclassifyCancel}
              data-testid="reclassify-cancel-btn"
            >
              Annulla
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleReclassifyKeepCode}
              className="border-[#e5e7eb] hover:border-[#FF7819] hover:text-[#FF7819]"
              data-testid="reclassify-keep-code-btn"
            >
              Mantieni codice
            </Button>
            <Button
              type="button"
              onClick={handleReclassifyRegenerateCode}
              className="btn-primary"
              data-testid="reclassify-regenerate-code-btn"
            >
              Rigenera codice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Import Modal */}
      <Dialog open={showImportModal} onOpenChange={(open) => !open && resetImport()}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-[#FF7819]" />
              Importa Listino Prezzi
            </DialogTitle>
            <DialogDescription>
              Importa voci da file CSV o Excel (formato Primus ACCA compatibile)
            </DialogDescription>
          </DialogHeader>

          {/* Step 1: Upload File */}
          {importStep === 1 && (
            <div className="space-y-4">
              <div 
                className="border-2 border-dashed border-[#DCDCDC] rounded-lg p-8 text-center hover:border-[#FF7819] transition-colors cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="w-12 h-12 mx-auto text-[#737373] mb-4" />
                <p className="text-lg font-medium mb-2">Trascina qui il file o clicca per selezionare</p>
                <p className="text-sm text-[#737373]">Formati supportati: CSV, Excel (.xlsx, .xls)</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  className="hidden"
                  onChange={handleFileSelect}
                />
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm">
                <p className="font-medium text-blue-800 mb-2">Come esportare da Primus:</p>
                <ol className="list-decimal list-inside text-blue-700 space-y-1">
                  <li>Apri il listino in Primus</li>
                  <li>Vai su <strong>File → Esporta</strong> o <strong>Utilità → Esportazione</strong></li>
                  <li>Scegli formato <strong>CSV</strong> o <strong>Excel</strong></li>
                  <li>Carica il file qui</li>
                </ol>
              </div>
            </div>
          )}

          {/* Step 2: Multi-Sheet Selection (Excel) */}
          {importStep === 2 && sheetsPreview && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-[#737373]">
                  File: <strong>{importFile?.name}</strong> ({sheetsPreview.total_sheets} fogli)
                </p>
                <Button variant="ghost" size="sm" onClick={() => { setImportStep(1); setSheetsPreview(null); }}>
                  Cambia file
                </Button>
              </div>

              {/* Mapping colonne */}
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-sm font-medium mb-2">Mappatura colonne (auto-rilevata)</p>
                <div className="grid grid-cols-4 gap-2 text-xs">
                  <div><span className="text-[#737373]">Codice:</span> <strong>{importMapping.codice || '-'}</strong></div>
                  <div><span className="text-[#737373]">Descrizione:</span> <strong>{importMapping.descrizione || '-'}</strong></div>
                  <div><span className="text-[#737373]">U.M.:</span> <strong>{importMapping.unita_misura || '-'}</strong></div>
                  <div><span className="text-[#737373]">Prezzo:</span> <strong>{importMapping.prezzo || '-'}</strong></div>
                </div>
              </div>

              {/* Azioni rapide */}
              <div className="flex items-center justify-between">
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => selectAllSheets(true)}>
                    Seleziona Tutti
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => selectAllSheets(false)}>
                    Deseleziona Tutti
                  </Button>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="sovrascrivi-sheets"
                    checked={importOptions.sovrascrivi}
                    onChange={(e) => setImportOptions({ ...importOptions, sovrascrivi: e.target.checked })}
                    className="rounded border-gray-300"
                  />
                  <Label htmlFor="sovrascrivi-sheets" className="text-sm">Sovrascrivi esistenti</Label>
                </div>
              </div>

              {/* Lista fogli con voci */}
              <div className="border rounded-lg max-h-[400px] overflow-y-auto">
                {sheetsPreview.sheets.map((sheet) => {
                  const sheetState = selectedSheets[sheet.sheet_name] || { selected: false, items: [] };
                  const selectedItemsCount = sheetState.items.filter(i => i.selected).length;
                  const isExpanded = expandedSheets[sheet.sheet_name];
                  
                  return (
                    <div key={sheet.sheet_name} className="border-b last:border-b-0">
                      {/* Sheet header */}
                      <div 
                        className={`flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50 ${sheetState.selected ? 'bg-[#FFF0E5]/30' : ''}`}
                      >
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={sheetState.selected}
                            onChange={() => toggleSheetSelection(sheet.sheet_name)}
                            className="rounded border-gray-300 text-[#FF7819]"
                          />
                          <span className="font-medium">{sheet.sheet_name}</span>
                          <span className="text-sm text-[#737373]">
                            ({selectedItemsCount}/{sheet.total_items} voci)
                          </span>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => toggleExpandSheet(sheet.sheet_name)}
                        >
                          {isExpanded ? (
                            <ChevronDown className="w-4 h-4" />
                          ) : (
                            <ChevronRight className="w-4 h-4" />
                          )}
                          <span className="ml-1 text-xs">{isExpanded ? 'Chiudi' : 'Espandi'}</span>
                        </Button>
                      </div>
                      
                      {/* Items list (expanded) */}
                      {isExpanded && (
                        <div className="bg-gray-50 px-3 py-2 max-h-[200px] overflow-y-auto">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="text-left text-[#737373]">
                                <th className="w-8"></th>
                                <th className="py-1">Codice</th>
                                <th className="py-1">Descrizione</th>
                                <th className="py-1">U.M.</th>
                                <th className="py-1 text-right">Prezzo</th>
                              </tr>
                            </thead>
                            <tbody>
                              {sheetState.items.map((item, idx) => (
                                <tr 
                                  key={`import-item-${sheet.sheet_name}-${idx}-${item[importMapping.codice] || idx}`} 
                                  className={`border-t border-gray-200 ${item.selected ? '' : 'opacity-50'}`}
                                >
                                  <td className="py-1">
                                    <input
                                      type="checkbox"
                                      checked={item.selected}
                                      onChange={() => toggleItemSelection(sheet.sheet_name, idx)}
                                      className="rounded border-gray-300 text-[#FF7819]"
                                    />
                                  </td>
                                  <td className="py-1 font-mono">{item[importMapping.codice] || '-'}</td>
                                  <td className="py-1 truncate max-w-[200px]">{item[importMapping.descrizione] || '-'}</td>
                                  <td className="py-1">{item[importMapping.unita_misura] || '-'}</td>
                                  <td className="py-1 text-right">
                                    {item[importMapping.prezzo] ? `€ ${item[importMapping.prezzo]}` : '-'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Summary e azioni */}
              <div className="flex items-center justify-between bg-[#FFF0E5] rounded-lg p-3">
                <div className="text-sm">
                  <strong className="text-[#FF7819]">{getSelectedCount().selected}</strong>
                  <span className="text-[#737373]"> voci selezionate su </span>
                  <strong>{getSelectedCount().total}</strong>
                  <span className="text-[#737373]"> totali</span>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={resetImport}>Annulla</Button>
                <Button 
                  className="btn-primary" 
                  onClick={handleImportSheets}
                  disabled={importing || getSelectedCount().selected === 0}
                >
                  {importing ? 'Importazione...' : `Importa ${getSelectedCount().selected} voci`}
                </Button>
              </DialogFooter>
            </div>
          )}

          {/* Step 2: Column Mapping (CSV) */}
          {importStep === 2 && importPreview && !sheetsPreview && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-[#737373]">
                  File: <strong>{importFile?.name}</strong> ({importPreview.total_rows} righe)
                </p>
                <Button variant="ghost" size="sm" onClick={() => setImportStep(1)}>
                  Cambia file
                </Button>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-red-600">Codice *</Label>
                  <Select value={importMapping.codice} onValueChange={(v) => setImportMapping({...importMapping, codice: v})}>
                    <SelectTrigger><SelectValue placeholder="Seleziona colonna" /></SelectTrigger>
                    <SelectContent>
                      {importPreview.columns.filter(col => col && col.trim()).map(col => (
                        <SelectItem key={col} value={col}>{col}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-red-600">Descrizione *</Label>
                  <Select value={importMapping.descrizione} onValueChange={(v) => setImportMapping({...importMapping, descrizione: v})}>
                    <SelectTrigger><SelectValue placeholder="Seleziona colonna" /></SelectTrigger>
                    <SelectContent>
                      {importPreview.columns.filter(col => col && col.trim()).map(col => (
                        <SelectItem key={col} value={col}>{col}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Unità di Misura</Label>
                  <Select value={importMapping.unita_misura || '__none__'} onValueChange={(v) => setImportMapping({...importMapping, unita_misura: v === '__none__' ? '' : v})}>
                    <SelectTrigger><SelectValue placeholder="Seleziona colonna" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">-- Nessuna --</SelectItem>
                      {importPreview.columns.filter(col => col && col.trim()).map(col => (
                        <SelectItem key={col} value={col}>{col}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-red-600">Prezzo *</Label>
                  <Select value={importMapping.prezzo} onValueChange={(v) => setImportMapping({...importMapping, prezzo: v})}>
                    <SelectTrigger><SelectValue placeholder="Seleziona colonna" /></SelectTrigger>
                    <SelectContent>
                      {importPreview.columns.filter(col => col && col.trim()).map(col => (
                        <SelectItem key={col} value={col}>{col}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Categoria</Label>
                  <Select value={importMapping.categoria || '__default__'} onValueChange={(v) => setImportMapping({...importMapping, categoria: v === '__default__' ? '' : v})}>
                    <SelectTrigger><SelectValue placeholder="Seleziona colonna" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__default__">-- Usa default --</SelectItem>
                      {importPreview.columns.filter(col => col && col.trim()).map(col => (
                        <SelectItem key={col} value={col}>{col}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Categoria Default</Label>
                  <Select value={importOptions.categoriaDefault} onValueChange={(v) => setImportOptions({...importOptions, categoriaDefault: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(cat => (
                        <SelectItem key={cat.code} value={cat.code}>{cat.code} - {cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex items-center gap-2 pt-2">
                <Checkbox 
                  id="sovrascrivi" 
                  checked={importOptions.sovrascrivi}
                  onCheckedChange={(checked) => setImportOptions({...importOptions, sovrascrivi: checked})}
                />
                <Label htmlFor="sovrascrivi" className="text-sm cursor-pointer">
                  Sovrascrivi voci esistenti con stesso codice
                </Label>
              </div>

              {/* Preview Table */}
              <div className="border rounded-lg overflow-hidden">
                <p className="px-3 py-2 bg-gray-50 text-sm font-medium">Anteprima (prime 5 righe)</p>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        {importPreview.columns.filter(col => col && col.trim()).slice(0, 6).map(col => (
                          <TableHead key={col} className="text-xs whitespace-nowrap">{col}</TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {importPreview.preview.slice(0, 5).map((row, idx) => (
                        <TableRow key={row.codice || `preview-row-${idx}`}>
                          {importPreview.columns.filter(col => col && col.trim()).slice(0, 6).map(col => (
                            <TableCell key={col} className="text-xs py-1">{String(row[col] || '').substring(0, 50)}</TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={resetImport}>Annulla</Button>
                <Button 
                  className="btn-primary" 
                  onClick={handleImport}
                  disabled={importing || !importMapping.codice || !importMapping.descrizione || !importMapping.prezzo}
                >
                  {importing ? 'Importazione...' : `Importa ${importPreview.total_rows} voci`}
                </Button>
              </DialogFooter>
            </div>
          )}

          {/* Step 3: Result */}
          {importStep === 3 && importResult && (
            <div className="space-y-4">
              <div className={`p-6 rounded-lg text-center ${importResult.imported > 0 ? 'bg-green-50' : 'bg-amber-50'}`}>
                {importResult.imported > 0 ? (
                  <Check className="w-16 h-16 mx-auto text-green-500 mb-4" />
                ) : (
                  <AlertCircle className="w-16 h-16 mx-auto text-amber-500 mb-4" />
                )}
                <h3 className="text-xl font-semibold mb-2">Import Completato</h3>
                <div className="flex justify-center gap-8 text-lg">
                  <div>
                    <span className="font-bold text-green-600">{importResult.imported}</span>
                    <span className="text-[#737373] ml-1">importate</span>
                  </div>
                  <div>
                    <span className="font-bold text-amber-600">{importResult.skipped}</span>
                    <span className="text-[#737373] ml-1">saltate</span>
                  </div>
                </div>
              </div>

              {importResult.errors?.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="font-medium text-red-800 mb-2">Errori ({importResult.errors.length}):</p>
                  <ul className="text-sm text-red-700 max-h-32 overflow-y-auto">
                    {importResult.errors.map((err, idx) => (
                      <li key={`error-${idx}-${err.substring(0, 20)}`}>{err}</li>
                    ))}
                  </ul>
                </div>
              )}

              {importResult.skipped_items?.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="font-medium text-amber-800 mb-2">
                    Voci saltate ({importResult.skipped_items.length}):
                  </p>
                  <div className="max-h-48 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-amber-100 sticky top-0">
                        <tr>
                          <th className="text-left px-2 py-1">Foglio</th>
                          <th className="text-left px-2 py-1">Codice</th>
                          <th className="text-left px-2 py-1">Descrizione</th>
                          <th className="text-left px-2 py-1">Motivo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {importResult.skipped_items.map((item, idx) => (
                          <tr key={`skipped-${idx}`} className="border-b border-amber-200">
                            <td className="px-2 py-1 text-amber-700">{item.sheet}</td>
                            <td className="px-2 py-1 font-mono text-xs">{item.code}</td>
                            <td className="px-2 py-1 text-amber-800">{item.description}</td>
                            <td className="px-2 py-1 text-amber-600 italic">{item.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <DialogFooter>
                <Button className="btn-primary" onClick={resetImport}>Chiudi</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Duplicates Modal */}
      <Dialog open={showDuplicatesModal} onOpenChange={setShowDuplicatesModal}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-amber-500" />
              Voci Duplicate nel Listino
            </DialogTitle>
            <DialogDescription>
              {duplicateSearchType === 'same_category' && 'Voci con la stessa descrizione nella stessa categoria'}
              {duplicateSearchType === 'different_category' && 'Voci con la stessa descrizione in categorie diverse'}
              {duplicateSearchType === 'all' && 'Tutte le voci duplicate nel listino'}
            </DialogDescription>
          </DialogHeader>
          
          {duplicates && (
            <div className="space-y-4">
              {duplicates.total_groups === 0 ? (
                <div className="text-center py-8 text-green-600">
                  <Check className="w-12 h-12 mx-auto mb-2" />
                  <p className="font-medium">Nessun duplicato trovato!</p>
                  <p className="text-sm text-[#737373]">
                    {duplicateSearchType === 'same_category' && 'Non ci sono voci duplicate nella stessa categoria.'}
                    {duplicateSearchType === 'different_category' && 'Non ci sono voci duplicate in categorie diverse.'}
                    {duplicateSearchType === 'all' && 'Il listino non contiene voci duplicate.'}
                  </p>
                </div>
              ) : (
                <>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center justify-between">
                    <p className="font-medium text-amber-800">
                      Trovati {duplicates.total_groups} gruppi di voci duplicate
                    </p>
                    <span className="text-xs bg-amber-200 text-amber-800 px-2 py-1 rounded">
                      {duplicateSearchType === 'same_category' && 'Stessa categoria'}
                      {duplicateSearchType === 'different_category' && 'Categorie diverse'}
                      {duplicateSearchType === 'all' && 'Tutti'}
                    </span>
                  </div>
                  
                  <div className="space-y-4 max-h-[50vh] overflow-y-auto">
                    {duplicates.duplicates.map((group, groupIdx) => (
                      <div key={`dup-group-${group.description?.substring(0, 20) || groupIdx}-${groupIdx}`} className="border border-[#DCDCDC] rounded-lg overflow-hidden">
                        <div className="bg-[#F2F2F2] px-4 py-2 font-medium text-sm flex items-center justify-between">
                          <span>"{group.description}" - {group.count} occorrenze</span>
                          {group.categories && group.categories.length > 1 && (
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                              Categorie: {group.categories.join(', ')}
                            </span>
                          )}
                          {group.category && (
                            <span className="text-xs bg-gray-200 text-gray-700 px-2 py-0.5 rounded">
                              Categoria: {group.category}
                            </span>
                          )}
                        </div>
                        <table className="w-full text-sm">
                          <thead className="bg-[#FAFAFA]">
                            <tr>
                              <th className="text-left px-4 py-2">Codice</th>
                              <th className="text-left px-4 py-2">Categoria</th>
                              <th className="text-left px-4 py-2">Sottocategoria</th>
                              <th className="text-left px-4 py-2">U.M.</th>
                              <th className="text-right px-4 py-2">Prezzo</th>
                              <th className="w-20 px-4 py-2"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {group.items.map((item, itemIdx) => (
                              <tr key={item.id} className={itemIdx > 0 ? 'bg-amber-50' : ''}>
                                <td className="px-4 py-2 font-mono">{item.code}</td>
                                <td className="px-4 py-2">{item.category}</td>
                                <td className="px-4 py-2">{item.subcategory || '-'}</td>
                                <td className="px-4 py-2">{item.unit}</td>
                                <td className="px-4 py-2 text-right">{formatCurrency(item.unit_price)}</td>
                                <td className="px-4 py-2">
                                  {itemIdx > 0 && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleDeleteDuplicate(item.id)}
                                      className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 px-2"
                                      title="Elimina questa voce duplicata"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDuplicatesModal(false)}>
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PriceListPage;
